import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Trash2, ExternalLink } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AssessmentsTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null); // id being mutated
  const [filter, setFilter] = useState("pending"); // pending | completed | all

  const load = async () => {
    try {
      const { data } = await api.get("/admin/square-assessments");
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load assessments"); }
  };
  useEffect(() => { load(); }, []);

  const complete = async (a) => {
    if (!window.confirm(`Mark ${a.name}'s $25 assessment as PAID? This is your confirmation that Square received the payment.`)) return;
    setBusy(a.id);
    try {
      await api.post(`/admin/square-assessments/${a.id}/complete`);
      toast.success(`${a.name} marked as paid`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} from assessments list? This won't refund anything; it just clears the lead.`)) return;
    setBusy(a.id);
    try {
      await api.delete(`/admin/square-assessments/${a.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const filtered = items.filter((a) => {
    if (filter === "all") return true;
    if (filter === "completed") return a.status === "completed";
    return a.status === "square_pending";
  });

  const pendingCount = items.filter((a) => a.status === "square_pending").length;
  const completedCount = items.filter((a) => a.status === "completed").length;

  return (
    <div data-testid="tab-assessments">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display text-3xl">
          Assessments <span className="text-zinc-500 text-base">({filtered.length})</span>
        </h2>
        <div className="flex gap-1">
          {[
            { id: "pending", label: `Pending (${pendingCount})` },
            { id: "completed", label: `Paid (${completedCount})` },
            { id: "all", label: `All (${items.length})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`assessments-filter-${f.id}`}
              className={`px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase border transition-colors ${
                filter === f.id
                  ? "border-[#E5B43A] text-[#E5B43A]"
                  : "border-zinc-800 text-zinc-500 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-zinc-500 mb-4 leading-relaxed max-w-2xl">
        Every $25 assessment signup lands here. When Square emails you the payment receipt, click
        <b className="text-zinc-300"> Mark Paid</b> on the row to track it as completed.
      </p>

      <div className="border border-zinc-800 bg-[#141414] overflow-x-auto">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="sticky top-0 bg-[#0A0A0A] z-10">
              <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-5 py-3 font-bold">Name</th>
                <th className="text-left px-5 py-3 font-bold">Email</th>
                <th className="text-left px-5 py-3 font-bold hidden lg:table-cell">Phone</th>
                <th className="text-left px-5 py-3 font-bold">Signed Up</th>
                <th className="text-left px-5 py-3 font-bold">Status</th>
                <th className="text-right px-5 py-3 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-zinc-500">
                    No {filter === "all" ? "" : filter} assessments yet.
                  </td>
                </tr>
              ) : filtered.map((a) => {
                const isPending = a.status === "square_pending";
                return (
                  <tr
                    key={a.id}
                    className="border-b border-zinc-900 hover:bg-[#1a1a1a] transition-colors"
                    data-testid={`assessment-row-${a.id}`}
                  >
                    <td className="px-5 py-4 font-semibold whitespace-nowrap">{a.name}</td>
                    <td className="px-5 py-4 text-zinc-300">
                      <a href={`mailto:${a.email}`} className="hover:text-[#E5B43A]">{a.email}</a>
                    </td>
                    <td className="px-5 py-4 text-zinc-400 hidden lg:table-cell whitespace-nowrap">{a.phone || "—"}</td>
                    <td className="px-5 py-4 text-zinc-500 text-xs whitespace-nowrap">{fmtDate(a.created_at)}</td>
                    <td className="px-5 py-4">
                      {isPending ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-amber-400 border border-amber-400/40 px-2 py-1">
                          Pending Pay
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400 border border-emerald-400/40 px-2 py-1">
                          <CheckCircle2 size={10} /> Paid
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {isPending && (
                          <button
                            onClick={() => complete(a)}
                            disabled={busy === a.id}
                            data-testid={`mark-paid-${a.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-300 border border-emerald-400/30 px-2.5 py-1.5 hover:bg-emerald-400/10 transition-colors disabled:opacity-40"
                          >
                            <CheckCircle2 size={11} /> Mark Paid
                          </button>
                        )}
                        <a
                          href={`mailto:${a.email}`}
                          className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 border border-zinc-700 px-2.5 py-1.5 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
                        >
                          <ExternalLink size={11} /> Email
                        </a>
                        <button
                          onClick={() => remove(a)}
                          disabled={busy === a.id}
                          data-testid={`delete-assessment-${a.id}`}
                          className="inline-flex items-center justify-center w-7 h-7 text-zinc-500 border border-zinc-800 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors disabled:opacity-40"
                          title="Remove"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
